import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  assignRestaurantManager,
  getEligibleRestaurantManagers,
} from '@/services/userRestaurantAssignmentApi';

function managerState(manager, restaurantId) {
  if (manager.restaurant_id === restaurantId) return 'thisRestaurant';
  if (manager.assignment_status === 'assigned' || manager.restaurant_id) return 'anotherRestaurant';
  return 'unassigned';
}

export default function RestaurantManagerAssignment({ completion, restaurantName, user, t }) {
  const restaurantId = completion?.restaurant?.id;
  const authenticatedUserId = user?.id ?? user?._id;
  const currentUserIdRef = useRef(authenticatedUserId);
  const requestGenerationRef = useRef(0);
  const submitInFlightRef = useRef(false);
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [isLoading, setIsLoading] = useState(Boolean(restaurantId));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageKey, setMessageKey] = useState(null);
  const [successManagerId, setSuccessManagerId] = useState(null);
  const [deferred, setDeferred] = useState(false);
  currentUserIdRef.current = authenticatedUserId;

  const loadManagers = useCallback(async ({ generation, userId } = {}) => {
    if (!restaurantId) return null;
    const expectedGeneration = generation ?? requestGenerationRef.current;
    const expectedUserId = userId ?? authenticatedUserId;
    try {
      const result = await getEligibleRestaurantManagers();
      if (
        requestGenerationRef.current !== expectedGeneration
        || currentUserIdRef.current !== expectedUserId
      ) return null;
      const nextManagers = Array.isArray(result) ? result : [];
      setManagers(nextManagers);
      setSelectedManagerId((current) => (
        nextManagers.some((manager) => manager.id === current && managerState(manager, restaurantId) === 'unassigned')
          ? current
          : ''
      ));
      return nextManagers;
    } catch (error) {
      if (
        requestGenerationRef.current === expectedGeneration
        && currentUserIdRef.current === expectedUserId
      ) setMessageKey('managerAssignmentLoadError');
      return null;
    }
  }, [authenticatedUserId, restaurantId]);

  useEffect(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    submitInFlightRef.current = false;
    setManagers([]);
    setSelectedManagerId('');
    setMessageKey(restaurantId ? null : 'managerAssignmentMissingRestaurant');
    setSuccessManagerId(null);
    setDeferred(false);
    setIsSubmitting(false);
    setIsLoading(Boolean(restaurantId));
    if (restaurantId) {
      loadManagers({ generation, userId: authenticatedUserId }).finally(() => {
        if (
          requestGenerationRef.current === generation
          && currentUserIdRef.current === authenticatedUserId
        ) setIsLoading(false);
      });
    }
    return () => {
      if (requestGenerationRef.current === generation) requestGenerationRef.current += 1;
    };
  }, [authenticatedUserId, loadManagers, restaurantId]);

  const handleAssign = async () => {
    if (!restaurantId || !selectedManagerId || submitInFlightRef.current) return;
    const selected = managers.find((manager) => manager.id === selectedManagerId);
    if (!selected || managerState(selected, restaurantId) !== 'unassigned') return;

    const requestUserId = authenticatedUserId;
    const generation = requestGenerationRef.current;
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setMessageKey(null);
    setDeferred(false);
    try {
      const assigned = await assignRestaurantManager(selectedManagerId, restaurantId);
      if (
        currentUserIdRef.current !== requestUserId
        || requestGenerationRef.current !== generation
      ) return;
      setManagers((current) => current.map((manager) => (
        manager.id === selectedManagerId ? { ...manager, ...assigned } : manager
      )));
      setSuccessManagerId(selectedManagerId);
      setSelectedManagerId('');
      setMessageKey('managerAssignmentSuccess');
    } catch (error) {
      if (
        currentUserIdRef.current !== requestUserId
        || requestGenerationRef.current !== generation
      ) return;
      const status = error?.response?.status;
      const ambiguousFailure = !error?.response || (status >= 500 && status !== 503);
      if (status === 404 || status === 409 || ambiguousFailure) {
        const authoritativeManagers = await loadManagers({ generation, userId: requestUserId });
        if (
          currentUserIdRef.current !== requestUserId
          || requestGenerationRef.current !== generation
        ) return;
        const authoritativeSelected = authoritativeManagers?.find(
          (manager) => manager.id === selectedManagerId
        );
        if (ambiguousFailure && authoritativeSelected?.restaurant_id === restaurantId) {
          setSuccessManagerId(selectedManagerId);
          setSelectedManagerId('');
          setMessageKey('managerAssignmentSuccess');
        } else {
          setMessageKey(status === 404 || status === 409
            ? 'managerAssignmentChanged'
            : 'managerAssignmentUnable');
        }
      } else if (status === 401 || status === 403) {
        setMessageKey('managerAssignmentAuthorizationError');
      } else if (status === 422) {
        setMessageKey('managerAssignmentValidationError');
      } else if (status === 503) {
        setMessageKey('managerAssignmentUnavailable');
      } else {
        setMessageKey('managerAssignmentUnable');
      }
    } finally {
      if (
        currentUserIdRef.current === requestUserId
        && requestGenerationRef.current === generation
      ) setIsSubmitting(false);
      if (requestGenerationRef.current === generation) submitInFlightRef.current = false;
    }
  };

  return (
    <section className="space-y-4 rounded-lg border p-4" aria-labelledby="manager-assignment-heading">
      <div>
        <h2 id="manager-assignment-heading" className="font-semibold">
          {t('restaurantOnboarding.assignRestaurantManager')}
        </h2>
        {restaurantName && <p className="mt-1 text-sm text-muted-foreground">{restaurantName}</p>}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('restaurantOnboarding.loadingManagers')}
        </div>
      ) : managers.length === 0 && restaurantId ? (
        <div className="text-sm text-muted-foreground">
          <p>{t('restaurantOnboarding.noManagersAvailable')}</p>
          <p>{t('restaurantOnboarding.assignManagerLater')}</p>
        </div>
      ) : restaurantId ? (
        <div className="space-y-3">
          <Label htmlFor="restaurant-manager-select">{t('restaurantOnboarding.selectManager')}</Label>
          <select
            id="restaurant-manager-select"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={selectedManagerId}
            disabled={isSubmitting}
            onChange={(event) => setSelectedManagerId(event.target.value)}
          >
            <option value="">{t('restaurantOnboarding.selectManager')}</option>
            {managers.map((manager) => {
              const state = managerState(manager, restaurantId);
              const stateLabel = state === 'thisRestaurant'
                ? t('restaurantOnboarding.assignedToThisRestaurant')
                : state === 'anotherRestaurant'
                  ? t('restaurantOnboarding.assignedToAnotherRestaurant')
                  : '';
              return (
                <option key={manager.id} value={manager.id} disabled={state !== 'unassigned'}>
                  {manager.name} ({manager.email}){stateLabel ? ` — ${stateLabel}` : ''}
                </option>
              );
            })}
          </select>
        </div>
      ) : null}

      {managers.some((manager) => managerState(manager, restaurantId) === 'thisRestaurant') && (
        <p className="text-sm text-emerald-700" role="status">
          {t('restaurantOnboarding.assignedToThisRestaurant')}
        </p>
      )}

      {successManagerId && (
        <div className="flex items-center gap-2 text-sm text-emerald-700" role="status">
          <CheckCircle2 className="h-4 w-4" />
          {t('restaurantOnboarding.assigned')}
        </div>
      )}
      {messageKey && (
        <p className={messageKey === 'managerAssignmentSuccess' ? 'text-sm text-emerald-700' : 'text-sm text-destructive'} role={messageKey === 'managerAssignmentSuccess' ? 'status' : 'alert'}>
          {t(`restaurantOnboarding.${messageKey}`)}
        </p>
      )}
      {deferred && <p className="text-sm text-muted-foreground" role="status">{t('restaurantOnboarding.assignManagerLater')}</p>}

      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={!selectedManagerId || isSubmitting || !restaurantId} onClick={handleAssign}>
          {isSubmitting ? t('restaurantOnboarding.assigningManager') : t('restaurantOnboarding.assignManager')}
        </Button>
        <Button type="button" variant="outline" disabled={isSubmitting} onClick={() => setDeferred(true)}>
          {t('restaurantOnboarding.assignLater')}
        </Button>
      </div>
    </section>
  );
}

export { managerState };
